/**
 * SequenceChainSVG — Phase 1, Component 6 of the TeachBud Representation Catalog
 *
 * Renders a sequence chain (boxes → arrows → boxes) per §5.2 Sequence & Summary:
 *   - 3–5 boxes arranged left to right, connected by arrows
 *   - Labels: First | Next | Then | Finally (or numbered 1–N)
 *   - Grades 2–3: large boxes with 3 lines each
 *   - Grades 4–8: standard boxes with 4–5 lines each
 *   - Student mode: boxes are empty with ruled lines
 *   - Teacher mode: box content provided via `steps` prop
 *   - Multi-subject: used for narrative sequence, science processes, SS chronology
 */

import React from 'react';

export interface SequenceChainProps {
  /** Number of steps/boxes. Default 4. */
  steps?: number;
  /** Content for each box in teacher/example mode. */
  stepContent?: (string | null)[];
  /** Custom labels below each box. Default: First / Next / Then / Finally. */
  stepLabels?: string[];
  /** When true, boxes are empty with ruled lines. */
  studentMode?: boolean;
  /** Number of ruled lines per box. Default 3. */
  linesPerBox?: number;
  /** Override pixel width. */
  width?: number;
  /** Grade (0=K). Adjusts sizing. */
  grade?: number;
  /** Box fill color. Default white with blue-tinted border. */
  boxColor?: string;
  className?: string;
}

// ── Layout ─────────────────────────────────────────────────────────────────────
const DEFAULT_LABELS = ['First', 'Next', 'Then', 'Finally', 'Last'];
const VB_W = 600;
const PAD  = 16;

// Box dimensions (scales with step count)
function boxDims(steps: number, grade: number) {
  const availW = VB_W - 2 * PAD;
  const arrowW = 28;
  const totalArrows = steps - 1;
  const boxW = Math.floor((availW - totalArrows * arrowW) / steps);
  const boxH = grade <= 2 ? 120 : grade <= 4 ? 100 : 88;
  return { boxW, boxH, arrowW };
}

const BOX_FILL    = '#FFFFFF';
const BOX_STROKE  = '#3B82F6';  // TeachBud blue
const BOX_STROKE2 = '#DBEAFE';  // blue-100 lighter variant
const ARROW_COLOR = '#6B7280';
const LABEL_COLOR = '#374151';
const LINE_COLOR  = '#E5E7EB';  // gray-200 for ruled lines
const HEADER_FILL = '#EFF6FF';  // blue-50

export function SequenceChainSVG({
  steps: stepCount = 4,
  stepContent,
  stepLabels,
  studentMode = true,
  linesPerBox = 3,
  width,
  grade = 3,
  boxColor = BOX_FILL,
  className = '',
}: SequenceChainProps) {
  const nSteps = Math.max(2, Math.min(5, stepCount));
  const { boxW, boxH, arrowW } = boxDims(nSteps, grade);

  const LABEL_FS = grade <= 2 ? 12 : 10;
  const CONTENT_FS = grade <= 2 ? 11 : 9;
  const LABEL_H = 24;
  const VB_H = PAD + boxH + LABEL_H + PAD;

  const labels = stepLabels ?? DEFAULT_LABELS.slice(0, nSteps);

  const svgW = width ?? VB_W;
  const svgH = width ? Math.round((VB_H / VB_W) * width) : VB_H;

  // Ruled lines inside each box
  const lineSpacing = boxH > 60 ? Math.floor((boxH - 32) / linesPerBox) : 0;

  return (
    <svg
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      width={svgW} height={svgH}
      className={className}
      role="img"
      aria-label={`Sequence chain with ${nSteps} steps`}
    >
      <title>Sequence chain ({nSteps} steps)</title>

      {Array.from({ length: nSteps }).map((_, i) => {
        const boxX = PAD + i * (boxW + arrowW);
        const boxY = PAD;
        const content = !studentMode ? (stepContent?.[i] ?? null) : null;
        const label = labels[i] ?? `Step ${i + 1}`;
        const isFinalStep = i === nSteps - 1;

        return (
          <g key={i}>
            {/* Box shadow */}
            <rect x={boxX + 2} y={boxY + 2} width={boxW} height={boxH} rx={8} fill="#E2E8F0" />

            {/* Box */}
            <rect
              x={boxX} y={boxY}
              width={boxW} height={boxH}
              rx={8}
              fill={boxColor}
              stroke={BOX_STROKE}
              strokeWidth={1.5}
            />

            {/* Header band */}
            <rect
              x={boxX} y={boxY}
              width={boxW} height={26}
              rx={8} ry={8}
              fill={HEADER_FILL}
              stroke={BOX_STROKE}
              strokeWidth={1.5}
            />
            {/* Clip bottom of header */}
            <rect x={boxX} y={boxY + 14} width={boxW} height={13} fill={HEADER_FILL} stroke="none" />

            {/* Header label */}
            <text
              x={boxX + boxW / 2} y={boxY + 17}
              textAnchor="middle"
              fontSize={LABEL_FS}
              fontFamily="Nunito, sans-serif"
              fontWeight={700}
              fill={BOX_STROKE}
            >
              {label}
            </text>

            {/* Content or ruled lines */}
            {!studentMode && content ? (
              <foreignObject x={boxX + 4} y={boxY + 30} width={boxW - 8} height={boxH - 34}>
                <div
                  style={{
                    fontSize: CONTENT_FS + 'px',
                    fontFamily: 'Inter, sans-serif',
                    color: '#1E3A5F',
                    lineHeight: 1.4,
                    padding: '2px 4px',
                    wordBreak: 'break-word',
                  }}
                >
                  {content}
                </div>
              </foreignObject>
            ) : (
              Array.from({ length: linesPerBox }).map((_, li) => {
                const ly = boxY + 32 + li * lineSpacing + lineSpacing / 2;
                if (ly > boxY + boxH - 8) return null;
                return (
                  <line
                    key={li}
                    x1={boxX + 8} y1={ly}
                    x2={boxX + boxW - 8} y2={ly}
                    stroke={LINE_COLOR}
                    strokeWidth={1}
                  />
                );
              })
            )}

            {/* Arrow to next box */}
            {!isFinalStep && (
              <g>
                <line
                  x1={boxX + boxW} y1={boxY + boxH / 2}
                  x2={boxX + boxW + arrowW - 4} y2={boxY + boxH / 2}
                  stroke={ARROW_COLOR} strokeWidth={2.5} strokeLinecap="round"
                />
                <polygon
                  points={`${boxX + boxW + arrowW - 6},${boxY + boxH / 2 - 5} ${boxX + boxW + arrowW},${boxY + boxH / 2} ${boxX + boxW + arrowW - 6},${boxY + boxH / 2 + 5}`}
                  fill={ARROW_COLOR}
                />
              </g>
            )}
          </g>
        );
      })}
    </svg>
  );
}

// ── Hint parser ───────────────────────────────────────────────────────────────
export function parseSequenceChainHint(hint: string): SequenceChainProps {
  const h = hint.toLowerCase();
  const countMatch = h.match(/(\d+)\s*(steps?|boxes?|events?|stages?)/);
  const steps = countMatch ? Math.min(5, Math.max(2, parseInt(countMatch[1]))) : 4;
  return { steps, studentMode: true };
}
